/**
 * Mutation defaults (registered before restore so persisted, paused mutations can resume after a reload).
 * Each mutation writes optimistically in onMutate; the server dedupes sends on clientId.
 */
import type { Message, PostMessageRequest } from "@bundle/contracts";
import { ApiFailure, Messages, Outbox, Workspace, type ApiClient, type SendInput } from "@bundle/core";
import type { QueryClient } from "@tanstack/query-core";
import { keys, updateOutbox, updatePages, updateThreads, updateWorkspace } from "./keys.ts";
import { shouldRetrySend } from "./retry.ts";
import { registerDatabaseMutations } from "./databases.ts";
import { registerPageMutations } from "./pages.ts";
import { registerChannelMutations } from "./channels.ts";

/** `fileIds`: the server ids of the input's uploads, once they are all stored (platform/files/sendGate.ts). */
export type SendVars = { ws: string; meId: string; input: SendInput; fileIds?: string[] };
export type EditVars = { ws: string; channelId: string; messageId: string; text: string; at: number };
export type DeleteVars = { ws: string; channelId: string; messageId: string };
export type ReactVars = { ws: string; channelId: string; messageId: string; emoji: string; on: boolean; meId: string };
/** Pin / unpin (queued like reactions: works offline, delivered in order per message on reconnect). */
export type PinVars = { ws: string; channelId: string; messageId: string; on: boolean; meId: string; at: number };
export type ReadVars = { ws: string; channelId: string; seq: number };
/** "Mark unread" from the message at `seq` (queued like pins: works offline; a refusal refetches the workspace). */
export type UnreadVars = { ws: string; channelId: string; seq: number };

/**
 * Read and unread writes of one conversation run one at a time, in order (TanStack mutation scope): a "Mark unread"
 * queued offline and the "read" that follows when I come back must reach the server in that order.
 */
export const readScope = (channelId: string): { id: string } => ({ id: `read:${channelId}` });

/** Sends run one at a time, in order (TanStack mutation scope). */
export const SEND_SCOPE = { id: "outbox" };

export function applySentMessage(client: QueryClient, ws: string, m: Message): void {
  updateOutbox(client, ws, (o) => Outbox.reconcileSent(o, m));
  if (Workspace.isChannelLevel(m)) updatePages(client, ws, m.channelId, (p) => Messages.insertIntoPages(p, m));
  if (m.threadRootId !== undefined) {
    client.setQueryData<Messages.MessagePage>(keys.thread(ws, m.threadRootId), (p) =>
      p === undefined ? p : { ...p, messages: Messages.upsertMessage(p.messages, m) },
    );
  }
  updateWorkspace(client, ws, (c) => Workspace.applyNewMessage(c, m));
}

/** Told when the server refuses a send for good with a reason worth showing (`blocked`: a 1:1 DM with someone who blocked me, or whom I blocked). */
let sendRefused: ((code: string) => void) | undefined;
export function setSendRefusedHandler(handler: ((code: string) => void) | undefined): void {
  sendRefused = handler;
}

export function registerMutations(client: QueryClient, api: ApiClient): void {
  registerDatabaseMutations(client, api);
  registerPageMutations(client, api);
  registerChannelMutations(client, api);
  client.setMutationDefaults(["send"], {
    scope: SEND_SCOPE,
    retry: shouldRetrySend,
    mutationFn: (v: SendVars) => {
      const req: PostMessageRequest = { text: v.input.text, clientId: v.input.clientId };
      if (v.input.threadRootId !== undefined) req.threadRootId = v.input.threadRootId;
      if (v.input.alsoSendToChannel === true) req.alsoSendToChannel = true;
      if (v.fileIds !== undefined && v.fileIds.length > 0) req.fileIds = v.fileIds;
      // Stickers go by id (the server attaches its current snapshot); GIFs carry the provider's refs.
      if (v.input.sticker !== undefined) req.stickerId = v.input.sticker.id;
      if (v.input.gif !== undefined) req.gif = v.input.gif;
      return api.postMessage(v.ws, v.input.channelId, req);
    },
    onMutate: (v: SendVars) => {
      updateOutbox(client, v.ws, (o) => Outbox.insertPending(o, v.input, v.meId));
    },
    onSuccess: (m: Message, v: SendVars) => applySentMessage(client, v.ws, m),
    onError: (e: unknown, v: SendVars) => {
      updateOutbox(client, v.ws, (o) => Outbox.failPending(o, v.input.clientId));
      if (e instanceof ApiFailure && (e.code === "blocked" || e.code === "billing_required")) sendRefused?.(e.code);
    },
  });

  client.setMutationDefaults(["edit"], {
    mutationFn: (v: EditVars) => api.editMessage(v.ws, v.messageId, v.text),
    onMutate: (v: EditVars) => {
      const blocks = Outbox.plainBlocks(v.text);
      updatePages(client, v.ws, v.channelId, (p) => Messages.editInPages(p, v.messageId, v.text, blocks, v.at));
      updateThreads(client, v.ws, (page) => Messages.editInPages([page], v.messageId, v.text, blocks, v.at)[0] ?? page);
    },
    onSuccess: (m: Message, v: EditVars) => {
      updatePages(client, v.ws, v.channelId, (p) => Messages.mapMessage(p, m.id, () => m));
      updateThreads(client, v.ws, (page) => Messages.mapMessage([page], m.id, () => m)[0] ?? page);
    },
    onError: (_e: unknown, v: EditVars) => void client.invalidateQueries({ queryKey: keys.messages(v.ws, v.channelId) }),
  });

  client.setMutationDefaults(["delete"], {
    mutationFn: (v: DeleteVars) => api.deleteMessage(v.ws, v.messageId),
    onMutate: (v: DeleteVars) => {
      updatePages(client, v.ws, v.channelId, (p) => Messages.deleteInPages(p, v.messageId));
      updateThreads(client, v.ws, (page) => Messages.deleteInPages([page], v.messageId)[0] ?? page);
    },
    onError: (_e: unknown, v: DeleteVars) => void client.invalidateQueries({ queryKey: keys.messages(v.ws, v.channelId) }),
  });

  client.setMutationDefaults(["react"], {
    mutationFn: (v: ReactVars) => (v.on ? api.addReaction(v.ws, v.messageId, v.emoji) : api.removeReaction(v.ws, v.messageId, v.emoji)),
    onMutate: (v: ReactVars) => {
      updatePages(client, v.ws, v.channelId, (p) => Messages.toggleReactionInPages(p, v.messageId, v.emoji, v.meId, v.on));
      updateThreads(client, v.ws, (page) => Messages.toggleReactionInPages([page], v.messageId, v.emoji, v.meId, v.on)[0] ?? page);
    },
    onError: (_e: unknown, v: ReactVars) => void client.invalidateQueries({ queryKey: keys.messages(v.ws, v.channelId) }),
  });

  client.setMutationDefaults(["pin"], {
    mutationFn: (v: PinVars) => api.setPinned(v.ws, v.messageId, v.on),
    onMutate: (v: PinVars) => {
      const by = v.on ? v.meId : undefined;
      updatePages(client, v.ws, v.channelId, (p) => Messages.pinInPages(p, v.messageId, by, v.at));
      updateThreads(client, v.ws, (page) => Messages.pinInPages([page], v.messageId, by, v.at)[0] ?? page);
    },
    onSuccess: (m: Message, v: PinVars) => {
      updatePages(client, v.ws, v.channelId, (p) => Messages.mapMessage(p, m.id, () => m));
      updateThreads(client, v.ws, (page) => Messages.mapMessage([page], m.id, () => m)[0] ?? page);
    },
    onError: (_e: unknown, v: PinVars) => void client.invalidateQueries({ queryKey: keys.messages(v.ws, v.channelId) }),
  });

  client.setMutationDefaults(["markRead"], {
    mutationFn: (v: ReadVars) => api.markRead(v.ws, v.channelId, v.seq),
    onMutate: (v: ReadVars) => updateWorkspace(client, v.ws, (c) => Workspace.applyRead(c, v.channelId, v.seq)),
  });

  client.setMutationDefaults(["markUnread"], {
    mutationFn: (v: UnreadVars) => api.markUnread(v.ws, v.channelId, v.seq),
    onMutate: (v: UnreadVars) => updateWorkspace(client, v.ws, (c) => Workspace.applyUnread(c, v.channelId, v.seq)),
    // Refused (e.g. no longer a member): the server's read marker wins.
    onError: (_e: unknown, v: UnreadVars) => void client.invalidateQueries({ queryKey: keys.workspace(v.ws) }),
  });
}
